import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import { Button } from '@resource-kit/ui/react';

export type ConfirmType = 'default' | 'warning' | 'danger' | 'info';

export interface ConfirmOptions {
  title: string;
  message: string;
  type?: ConfirmType;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  /** Optional test selector for Playwright hooks on the primary action. */
  confirmTestId?: string;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (title: string, message: string, type?: ConfirmType) => Promise<void>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

const ConfirmIcon = ({ type }: { type: ConfirmType }) => {
  const iconMap = {
    default: Info,
    info: Info,
    warning: AlertTriangle,
    danger: XCircle,
  };

  const colorMap = {
    default: 'text-accent',
    info: 'text-accent',
    warning: 'text-warning',
    danger: 'text-destructive',
  };

  const Icon = iconMap[type];
  return <Icon className={`w-6 h-6 ${colorMap[type]}`} />;
};

interface ConfirmDialogProps {
  isOpen: boolean;
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  options,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const {
    title,
    message,
    type = 'default',
    confirmText: confirmTextProp,
    cancelText: cancelTextProp,
    confirmTestId,
  } = options;
  const confirmText = confirmTextProp ?? t('ui.confirm');
  const cancelText = cancelTextProp ?? t('ui.cancel');

  const colorMap = {
    default: 'border-accent/20 bg-accent/5',
    info: 'border-accent/20 bg-accent/5',
    warning: 'border-warning/20 bg-warning/5',
    danger: 'border-destructive/20 bg-destructive/5',
  };

  return (
    // z-[60] sits above Shadcn Sheet/Dialog primitives (z-50). Without
    // this, opening a confirm from inside a Sheet (e.g. the exceptions
    // resolve flow) would render the confirm UNDER the Sheet's portal —
    // the Sheet's overlay/content captures pointer events first and
    // the confirm's primary action is unclickable. Bumping just the
    // confirm overlay keeps every other modal at z-50 and removes
    // the layering ambiguity for nested confirmation flows.
    <div className="fixed inset-0 bg-overlay/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full shadow-xl">
        <div className={`p-6 border-b ${colorMap[type]}`}>
          <div className="flex items-center gap-3">
            <ConfirmIcon type={type} />
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          </div>
        </div>

        <div className="p-6">
          <p className="text-muted-foreground leading-relaxed">{message}</p>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
          {cancelText && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {cancelText}
            </Button>
          )}
          <Button
            data-testid={confirmTestId}
            onClick={onConfirm}
            disabled={loading}
            variant={type === 'danger' ? 'destructive' : 'default'}
            className={
              type === 'warning' ? 'bg-warning hover:bg-warning/80 text-warning-foreground' : ''
            }
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                {t('ui.processing')}
              </>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface Entry {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
  loading: boolean;
}

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<Entry[]>([]);
  const dialog = queue[0];

  // Mount-stable: this context exposes no state, so no consumer of
  // `useConfirm()` has any reason to be invalidated by a dialog opening.
  // `setQueue` is React state and permanently stable.
  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setQueue((prev) => [...prev, { options, resolve, loading: false }]);
    });
  }, []);

  // `t` is the one real dependency — the OK label is baked in at call time, so
  // an alert opened after a language switch has to use the new bundle.
  const alert = useCallback(
    (title: string, message: string, type: ConfirmType = 'info'): Promise<void> => {
      return new Promise((resolve) => {
        setQueue((prev) => [
          ...prev,
          {
            options: {
              title,
              message,
              type,
              confirmText: t('ui.ok'),
              cancelText: '',
            },
            resolve: () => resolve(),
            loading: false,
          },
        ]);
      });
    },
    [t],
  );

  const handleConfirm = async () => {
    if (!dialog || dialog.loading) return;

    try {
      setQueue((prev) =>
        prev.map((entry, index) => (index === 0 ? { ...entry, loading: true } : entry)),
      );

      if (dialog.options.onConfirm) {
        await dialog.options.onConfirm();
      }

      dialog.resolve(true);
      setQueue((prev) => prev.slice(1));
    } catch (error) {
      setQueue((prev) =>
        prev.map((entry, index) => (index === 0 ? { ...entry, loading: false } : entry)),
      );
      console.error('Confirm action failed:', error);
    }
  };

  const handleCancel = () => {
    if (!dialog || dialog.loading) return;

    if (dialog.options.onCancel) {
      dialog.options.onCancel();
    }

    dialog.resolve(false);
    setQueue((prev) => prev.slice(1));
  };

  const contextValue = useMemo<ConfirmContextType>(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      {dialog && (
        <ConfirmDialog
          isOpen={true}
          options={dialog.options}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          loading={dialog.loading}
        />
      )}
    </ConfirmContext.Provider>
  );
};
